import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminBlogAPI } from '../services/api';
import { useAlert } from '../context/AlertContext';
import BlogModal from '../components/BlogModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import '../styles/Blogs.css';

const Blogs = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [blogToDelete, setBlogToDelete] = useState(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchBlogs = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 10,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { status: statusFilter })
      };

      const response = await adminBlogAPI.getAll(params);
      setBlogs(response.data.data || []);
      setTotalPages(response.data.pagination.totalPages || 1);
      setError(null);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError(err.response?.data?.message || 'Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleCreate = () => {
    setSelectedBlog(null);
    setShowModal(true);
  };

  const handleEdit = (blog) => {
    setSelectedBlog(blog);
    setShowModal(true);
  };

  const handleDeleteClick = (blog) => {
    setBlogToDelete(blog);
    setShowDeleteModal(true);
  };

  const handleBlogDeleted = () => {
    setShowDeleteModal(false);
    setBlogToDelete(null);
    fetchBlogs();
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      if (currentStatus === 'Published') {
        await adminBlogAPI.unpublish(id);
      } else {
        await adminBlogAPI.publish(id);
      }
      fetchBlogs();
    } catch (err) {
      console.error('Error updating status:', err);
      showAlert(err.response?.data?.message || 'Failed to update blog status', 'error');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && blogs.length === 0) {
    return (
      <div className="blogs-container">
        <div className="loading">Loading blogs...</div>
      </div>
    );
  }

  return (
    <div className="blogs-container">
      <div className="blogs-header">
        <h1>Blog Management</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Add New Blog
        </button>
      </div>

      <div className="blogs-toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search blogs..."
            value={search}
            onChange={handleSearch}
          />
        </div>

        <div className="filters">
          <select value={statusFilter} onChange={handleStatusFilterChange}>
            <option value="">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Published">Published</option>
            <option value="Archived">Archived</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {blogs.length === 0 ? (
        <div className="empty-state">
          <p>No blogs found</p>
          <button className="btn btn-primary" onClick={handleCreate}>
            Create Your First Blog
          </button>
        </div>
      ) : (
        <div className="blogs-table-wrapper">
          <table className="blogs-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Category</th>
                <th>Author</th>
                <th>Status</th>
                <th>Published</th>
                <th>Views</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.map((blog) => (
                <tr key={blog._id}>
                  <td>
                    <div className="blog-image">
                      {blog.featuredImage ? (
                        <img
                          src={blog.featuredImage}
                          alt={blog.title}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="no-image">No Image</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="blog-title-cell">
                      <h3>{blog.title}</h3>
                      <p className="blog-excerpt-short">{blog.excerpt}</p>
                    </div>
                  </td>
                  <td>
                    <span className="category-badge">{blog.category}</span>
                  </td>
                  <td>
                    <div className="author-cell">{blog.author}</div>
                  </td>
                  <td>
                    <span className={`status-badge ${blog.status.toLowerCase()}`}>{blog.status}</span>
                  </td>
                  <td>
                    <div className="date-cell">
                      {blog.publishedAt ? formatDate(blog.publishedAt) : "-"}
                    </div>
                  </td>
                  <td>
                    <div className="views-cell">{blog.viewCount || 0}</div>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="action-btn btn-view-icon"
                        onClick={() => navigate(`/blogs/${blog._id}`)}
                        title="View"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>
                      <button
                        className="action-btn btn-edit-icon"
                        onClick={() => handleEdit(blog)}
                        title="Edit"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button
                        className="action-btn btn-toggle-icon"
                        onClick={() =>
                          handleStatusToggle(blog._id, blog.status)
                        }
                        title={
                          blog.status === "Published" ? "Unpublish" : "Publish"
                        }
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          strokeWidth="2"
                          stroke={
                            blog.status === "Published"
                              ? "#22c55e"
                              : blog.status === "Draft"
                                ? "#9ca3af"
                                : "#ef4444"
                          }
                        >
                          <rect
                            x="1"
                            y="5"
                            width="22"
                            height="14"
                            rx="7"
                            ry="7"
                          ></rect>
                          <circle
                            cx={blog.status === "Published" ? "16" : "8"}
                            cy="12"
                            r="3"
                            fill={
                              blog.status === "Published"
                                ? "#22c55e"
                                : blog.status === "Draft"
                                  ? "#9ca3af"
                                  : "#ef4444"
                            }
                          ></circle>
                        </svg>
                      </button>
                      <button
                        className="action-btn btn-delete-icon"
                        onClick={() => handleDeleteClick(blog)}
                        title="Delete"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}

      <BlogModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={fetchBlogs}
        blog={selectedBlog}
      />

      {showDeleteModal && (
        <DeleteConfirmModal
          item={blogToDelete}
          itemType="blog"
          onClose={() => {
            setShowDeleteModal(false);
            setBlogToDelete(null);
          }}
          onDeleted={handleBlogDeleted}
          deleteFunction={adminBlogAPI.delete}
        />
      )}
    </div>
  );
};

export default Blogs;
